// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Pick up, carry, snap, place (M13-07, 06 §9).
//
// The whole of ADR 0015 is in one gesture: a part is pinched off a plinth or off the board,
// carried in the hand, and let go over the playfield, where it lands on the point the snapping
// rules chose rather than the point the finger was at. This file is that gesture, and nothing
// else — it does not know what a hand looks like in Three.js, what the tray is made of, or how a
// ghost is drawn. It is handed fingertips in the **playfield's own frame** (PH-001: +X right, +Y
// up the playfield, +Z out of the surface, metres) and a `LayoutDraft`, and it hands back the next
// draft and what to draw.
//
// **The draft owns the edit; this owns the hand.** Every write to the definition goes through
// `layoutDraft.ts` — `beginDrag`, `dragTo`, `endDrag`, `cancelDrag`, `place` — so a part placed by
// hand is a part placed exactly as the 2-D canvas places one, snapped by the same `snapPoint`,
// wired by the same `place`, undone by the same history. A second edit model for the headset would
// be the machine-definition equivalent of two clients disagreeing about a placement.
//
// **A pinch is a decision only on its closing edge, and only on something.** The product's own
// finding: a relaxed hand reads as pinched (five of five heartbeats, `MachineInteraction`), so a
// hand that is *already* pinched when it arrives at a plinth takes nothing, and a hand that closes
// in mid-air takes nothing. `CabinetGrab` states the rule and starts its latches closed for the
// reason this file does: every hand has to be seen open once before it can take hold of anything.
//
// **Delta-carry, for a part that is already on the board.** A hand that closes 3 mm off a
// flipper's pivot is still 3 mm off it when it opens — `Grab.offset` in `layoutDraft.ts` — so the
// part does not jump to the fingertip on the first frame. A part off a plinth is different: it is
// *in the hand*, and the point the ghost rides is the fingertip's own, snapped.
//
// **Over the board or not is a volume, not a plane.** Inside the playfield's footprint, from a
// fingertip's depth below the surface to `OVER_BOARD` above the glass, a carry is proposing a
// place; outside it, the part is in the air and a release puts it back where it came from. Both
// halves matter: a release just off the edge must not plant a part on the rail, and a release a
// hand's height above the glass must not either, because that is where a hand goes when it is
// changing its mind.
//
// **A release the loader would refuse is refused here, with the loader's sentence** (2026-09-13,
// Quest 3). The 2-D canvas lets a draft be invalid between edits — its overlay says so and Test
// play checks before loading — but the headset's seam rebuilds the *session* on every commit, and
// `loadMachine` throws on a machine the validator rejects. A second pop bumper dropped near the
// first snaps onto its centre (a point match) and the two interpenetrate; the throw came up
// through the frame callback, Three stopped requesting frames, and the picture froze with the
// ghost in mid-air — reported as *"the part gets stuck mid-air and the pinball machine moves with
// the hand"*, the frozen layer riding the head. So the definition a release would write is run
// through `validateMachine` first, and a release that fails is a cancel with the first error as
// its sentence: the part goes back to its plinth, or the moved part back where it was.
//
// **Two hands on a two-ended part (M13-14).** While a slingshot, a target, a spinner or a gate is
// held by one of its ends, the other hand's closing edge within `HANDLE_CATCH` of its *other* end
// takes that end too, and the part follows both hands — turned, lengthened, or both. It is one
// gesture in the draft's history (`beginOtherDrag`, two `withAnchor` calls in `previewOf`), so one
// Undo takes the whole of it back. Either hand may let go first: its end settles where it was left
// (over the board) or goes back (in the air), and the other hand carries on — the first hand
// letting go hands the part to the second rather than dropping it. The last hand to open decides
// the gesture as a single hand always has: over the board writes it, in the air cancels it. A
// part off a plinth is one thing in one hand; it has no other end to take.
//
// **Back to the tray is off the machine.** A palette that only adds is a trap (`layoutDraft.remove`'s
// own words), and the bench had no way to take a part off: let go in the air and the part went back
// where it was. So a placed part carried into the **remove tray** — the open box hung off the
// cabinet's right front corner, which the tray reports as `atTray` — and let go there comes off the
// machine, with everything that belonged to it, through the Builder's own `remove`: one Undo puts it
// back. Nothing else on the tray removes (the owner, 24 Oct 2026).
//
// **The board wins** (24 Oct 2026, measured in Unity by `TrayReturnReachTests`). At bench scale the
// middle tiles and plinths hang over the playfield, down to 4 cm over the glass, so "the tray wins"
// would have removed a part an author was putting down. A hand at the tray removes only where it is
// *not* also over the board a part can be let go over (`carriedOver`); anywhere a part can be placed,
// it is placed. The box hangs beside the cabinet, clear of the board at every scale. The
// frame the hand arrives says `leaving`, so the host can say "let go here to take it off" before it
// happens; a removal the loader would refuse is refused with its sentence, like a release.

import {type EditorPart as MachineDevice,type PartKind as DeviceKind} from '../../builder/parts'
import { BENCH_EN, benchText, type BenchStrings } from './benchStrings'
import { validateMachine,   type Point2 } from '../../../core/ts/machine'
import {
  anchorAt,
  anchorOf,
  beginDrag,
  beginDragOn,
  beginOtherDrag,
  cancelDrag,
  devicesOf,
  dragOtherTo,
  dragTo,
  endDrag,
  place,
  previewOf,
  remove,
  releaseEnd,
  snapAgainst,
  type LayoutDraft,
} from '../../builder/layoutDraft'
import { makeDevice, type FlipperHand } from '../../builder/palette'
import { snapPoint, type SnapResult } from '../../builder/snapping'
import type { PlayfieldSize } from '../../builder/viewport'

/**
 * How near a fingertip has to be, in **room** metres, for a handle to be grabbed or a snap rule
 * to catch.
 *
 * Six millimetres — the recommendation's own "fingertip-sized ~6 mm in world metres". Divided by
 * the machine's scale before it reaches `snapPoint`, exactly as the 2-D canvas converts eight
 * pixels through `metresPerPixel`: at the bench's 0.33 that is 18 mm of playfield, at full size
 * 6 mm, and the finger feels the same catch at both. 18 mm is close to what the canvas's
 * `SNAP_PIXELS` comes to at an ordinary zoom (about 12 mm), which matters because `snapPoint`
 * ranks geometry over symmetry *within* the tolerance — a catch twice this wide pulled a part
 * meant for a mirror point onto the flippers' pivot line 30 mm away, in this file's own test.
 * **Judgement, not evidence** — M13-11 measures release accuracy on a device, and this is the
 * number it will move.
 */
export const CATCH_RADIUS = 0.006

/**
 * How near a fingertip has to be, in **room** metres, to a placed part's handle to take it.
 *
 * Fifteen millimetres — wider than `CATCH_RADIUS`, on purpose and by the second device day of
 * M13. Moving a placed part was in this file from the start (a closing edge over the board, within
 * the catch of a handle, begins a drag), and the author who used the mode for an hour asked for
 * it as a feature: at 6 mm in the room — 18 mm on the bench — with no sign of a handle, a part is
 * "not there". A snap has to stay tight or it pulls a part onto the wrong rule (this file's own
 * test); a *grab* is a different question, a fingertip reaching for a thing it can see, and
 * `beginDrag` now takes the two tolerances apart. Divided by the machine's scale like the other,
 * so the finger feels the same catch at 0.33 as at 1:1.
 */
export const HANDLE_CATCH = 0.015

/**
 * How far a lit handle stays lit, room metres (the 1.0.102 headset session, 26 Sep 2026).
 *
 * **What is lit is what a pinch takes.** A handle lights when the pinch point comes within
 * `HANDLE_CATCH` of it, and the pinch point is the middle of thumb and index — which moves as the
 * fingers close, by as much as half the gap they closed across. On the Quest the part under an
 * open hand lit, the fingers closed, the point slid off the 15 mm catch, and the pinch took
 * nothing: forty-seven hovers and one take in the log. So a lit handle stays lit until the hand
 * is this far from it, and a closing edge takes the lit handle when no other is within the catch.
 */
export const HANDLE_KEEP = 0.035

/** How far above the glass a carry is still over the board, room metres. */
export const OVER_BOARD = 0.06

/** How far into the surface a fingertip may press and still be over it, room metres. */
export const UNDER_BOARD = 0.02

/** How far past the playfield's edge still counts as over it, playfield metres. */
export const BOARD_MARGIN = 0.01

/**
 * How long after the hand was last in the remove tray a release there still takes the part off,
 * milliseconds (24 Oct 2026). On the native 1.0.207 every release at the tray went back as
 * "OffBoard": opening the fingers moves the fingertip the tray reads, and the tray sits low beside
 * the cabinet. A release back over the board, or a hand lost, still never removes.
 */
export const TRAY_GRACE_MS = 400

/** The volume in which a carried part is over the board, playfield-local metres. */
export interface CarryBoard {
  readonly width: number
  readonly length: number
  /** Highest z (out of the surface) that is still over the board. */
  readonly ceiling: number
  /** Lowest z — a fingertip pressed into the picture. Negative. */
  readonly floor: number
}

/**
 * The board volume for a machine drawn at `scale`.
 *
 * `glassHeight` is the cabinet's, in playfield metres; the room-metre bands are divided by the
 * scale so a hand's height above a bench-scale glass is judged in the hand's own metres.
 */
export function boardFor(playfield: PlayfieldSize, glassHeight: number, scale: number): CarryBoard {
  return {
    width: playfield.width,
    length: playfield.length,
    ceiling: glassHeight + OVER_BOARD / scale,
    floor: -UNDER_BOARD / scale,
  }
}

/**
 * Why the loader would refuse this draft, in the validator's first sentence — or nothing.
 *
 * Without the flood-fill classes: a release is judged on shape and overlap, which is what a
 * dropped part can break; a trap or an unreachable device is a warning the author reads in the
 * overlay (M13-17), not a reason to send the part back to its plinth.
 */
function unloadable(draft: LayoutDraft, strings: BenchStrings): string | undefined {
  const first = validateMachine(draft.definition).errors[0]
  return first === undefined ? undefined : benchText(strings, 'toast.notPlaced', { message: first.message })
}

/**
 * How high over the glass a part *in hand* is still over the board, room metres (the 1.0.103
 * headset round, 26 Sep 2026). A part is lifted to be moved; `OVER_BOARD`'s 6 cm let it go "in
 * the air" and sent it back where it was — two of the round's moves ended "not placed" with no
 * reason given. Taking and lighting still use `OVER_BOARD`.
 */
export const CARRY_ABOVE = 0.25

/** The board volume raised to `CARRY_ABOVE`: over the board for something already in hand, or aimed from above. */
export function raisedBoard(board: CarryBoard, scale: number): CarryBoard {
  return { ...board, ceiling: board.ceiling + (CARRY_ABOVE - OVER_BOARD) / (scale > 0 ? scale : 1) }
}

/** Why a carried part went back (the 1.0.113 round). */
export type CarryMiss = 'tooHigh' | 'offBoard' | 'lost'

/**
 * Why a release at this hand put nothing down, before any rule is asked: `lost` with no hand,
 * `tooHigh` over the board's footprint and above the raised ceiling, `offBoard` anywhere else
 * that is not over the board; nothing when the hand was over it.
 */
export function missOf(hand: { readonly present: boolean; readonly x: number; readonly y: number; readonly z: number } | undefined, options: CarryOptions): CarryMiss | undefined {
  if (hand === undefined || !hand.present) return 'lost'
  if (carriedOver(hand.x, hand.y, hand.z, options)) return undefined
  const board = raisedBoard(options.board, options.scale)
  const onFootprint = Math.abs(hand.x) <= board.width / 2 + BOARD_MARGIN && Math.abs(hand.y) <= board.length / 2 + BOARD_MARGIN
  return onFootprint && hand.z > board.ceiling ? 'tooHigh' : 'offBoard'
}

/** The bench's sentence for a miss. */
export function missedLine(miss: CarryMiss, strings: BenchStrings): string {
  return strings[miss === 'tooHigh' ? 'carry.tooHigh' : miss === 'offBoard' ? 'carry.offBoard' : 'carry.lost']
}

/** Is a playfield-local point over the board for a part already in hand — `overBoard`, higher? */
export function carriedOver(x: number, y: number, z: number, options: CarryOptions): boolean {
  return overBoard(x, y, z, raisedBoard(options.board, options.scale))
}

/** Is a playfield-local point inside the board volume? */
export function overBoard(x: number, y: number, z: number, board: CarryBoard): boolean {
  return (
    Math.abs(x) <= board.width / 2 + BOARD_MARGIN &&
    Math.abs(y) <= board.length / 2 + BOARD_MARGIN &&
    z >= board.floor &&
    z <= board.ceiling
  )
}

/** What the tray says a hand is at, if anything. */
export interface AtPlinth {
  readonly index: number
  readonly kind: DeviceKind
  /** A flipper plinth's side, when the plinth has one (26 Sep 2026). */
  readonly hand?: FlipperHand
  /** Why this part cannot be taken, in the palette's own words, or nothing. */
  readonly refusal: string | undefined
}

/** One hand, as this file needs it: a fingertip in the playfield's frame, a pinch, and a plinth. */
export interface CarryHand {
  readonly handedness: 'left' | 'right'
  readonly present: boolean
  readonly x: number
  readonly y: number
  readonly z: number
  /** Pinching. `HandTracker` has already turned an unmeasured pinch into `false`. */
  readonly grab: boolean
  readonly atPlinth: AtPlinth | undefined
  /**
   * In the remove tray (`TrayFrame.atTray`). A placed part let go here comes off the machine. Absent
   * is no.
   */
  readonly atTray?: boolean
}

/** Where the part in hand came from. */
export type CarrySource =
  /** Off a plinth: a part the machine does not have yet. */
  | { readonly kind: 'new'; readonly plinth: number; readonly device: DeviceKind; readonly hand?: FlipperHand }
  /** Off the board: a part the draft already has, held by one of its anchors. */
  | { readonly kind: 'moved'; readonly deviceId: string }

export interface CarryOptions {
  readonly board: CarryBoard
  readonly playfield: PlayfieldSize
  /** The machine's scale in the room, for `CATCH_RADIUS`. */
  readonly scale: number
  /** Mirror snapping on. The Build page's Symmetry row. */
  readonly symmetry: boolean
  /** The language a refusal is worded in (M13-26). English absent. */
  readonly strings?: BenchStrings
  /** This frame's length, ms: what a held control repeats by (the mini-map's arrows). Absent is 0, no repeat. */
  readonly frameMs?: number
}

/** What happened this frame, and what to draw. */
export interface CarryFrame {
  readonly draft: LayoutDraft
  /** Which hand is carrying, or nothing. */
  readonly held: 'left' | 'right' | undefined
  /** The second hand, on the other end of the part in hand (M13-14) — or nothing. */
  readonly second: 'left' | 'right' | undefined
  readonly source: CarrySource | undefined
  /** True on the frame a carry began — the click, the flash, the plinth going dark. */
  readonly took: boolean
  /** True on the frame a second hand took the other end (M13-14): a click, and a line. */
  readonly joined: boolean
  /** True on the frame a carry ended, however it ended. */
  readonly dropped: boolean
  /** True on the frame a release wrote the definition: a new part, a moved one, or one taken off. */
  readonly committed: boolean
  /** The id of the part a release at the tray took off the machine, on that frame — or nothing. */
  readonly removed?: string | undefined
  /**
   * True on the frame a placed part in hand arrives at the tray: let go now and it comes off. The
   * host says so — *"Let go here to take it off the machine"* — as the ramp's points do.
   */
  readonly leaving?: boolean
  /**
   * Why a release put nothing down, when it was the hand's place and not a rule (the 1.0.113
   * round, 27 Sep 2026: a part let go too high went back without a word): `tooHigh` over the
   * board's footprint but above `CARRY_ABOVE`, `offBoard` beside it, `lost` with the hand out of
   * sight. The host says it, in the bench's words (`missedLine`).
   */
  readonly missed?: CarryMiss | undefined
  /** The device as it would be if the hand opened now, or nothing while it is in the air. */
  readonly ghost: MachineDevice | undefined
  /**
   * The placed part in hand while it is off the board, drawn at the hand (24 Oct 2026, the owner
   * on 1.0.207: "the component stops at edge of board and can not be carried over to remove
   * bucket"). `ghost` stays undefined in the air — nothing would be placed — but the part is shown
   * going where the hand takes it, the remove tray included. One object for the whole time in the
   * air; `inHandShift` moves it: playfield metres from where it was taken into the air, and how
   * far over the playfield's plane the hand is (negative below it, as the tray is).
   */
  readonly inHand?: MachineDevice
  readonly inHandShift?: readonly [number, number, number]
  /** Why the ghost is where it is. Undefined while in the air. */
  readonly snap: SnapResult | undefined
  /**
   * The point the snap was asked about — where the fingers are, plus a moved part's grab offset
   * — for the reticle that shows a rule pulling the part off them (M13-13). Undefined with `snap`.
   */
  readonly raw: Point2 | undefined
  /** A plinth the hand pinched and could not take, in the palette's words. */
  readonly refused: string | undefined
  /**
   * The placed part whose handle is under a fingertip, while nothing is carried — the one a pinch
   * would take. The host lights it (M13-13), because a part that could be picked up has to look
   * like one. Undefined while carrying, or with no hand over a handle.
   */
  readonly hover: CarryHover | undefined
}

/** A placed part's handle under a fingertip. */
/** A handle lit under a hand: `anchorAt`'s answer, kept (`HANDLE_KEEP`). */
type Lit = { readonly deviceId: string; readonly role: string; readonly at: Point2; readonly shapes: boolean }

export interface CarryHover {
  readonly deviceId: string
  /** Which handle: the anchor's role, as `deviceAnchors` names it. */
  readonly role: string
  /** True for an end that stretches the part rather than moving it (M13-14). */
  readonly shapes: boolean
  readonly hand: 'left' | 'right'
}

export class PartCarry {
  private held: 'left' | 'right' | undefined
  /** The hand on the other end of a two-ended part, while both are held (M13-14). */
  private second: 'left' | 'right' | undefined
  private sourceNow: CarrySource | undefined
  /**
   * Whether each hand was pinched last frame — **starting closed, deliberately** (`CabinetGrab`).
   * A hand nobody has seen yet might already be shut, and the first frame of one must not be an
   * edge.
   */
  private readonly wasGrab = { left: true, right: true }
  /** The last ghost, kept while the snapped point does not move so the picture is not rebuilt. */
  private ghost: MachineDevice | undefined
  private ghostAt: Point2 | undefined
  private snap: SnapResult | undefined
  private raw: Point2 | undefined
  /** The handle lit under each hand while nothing is carried (`HANDLE_KEEP`). */
  private readonly lit: { left: Lit | undefined; right: Lit | undefined } = { left: undefined, right: undefined }
  /** Whether the placed part in hand was at the tray last frame, for `leaving`'s edge. */
  private atTrayNow = false
  /** Milliseconds since the placed part in hand was last at the tray (`TRAY_GRACE_MS`). */
  private sinceTrayMs = Number.POSITIVE_INFINITY
  /** The placed part in hand off the board (`inHand`), where the hand was when it left, and how far it has gone. */
  private inHand: MachineDevice | undefined
  private inHandFrom: Point2 | undefined
  private inHandShift: [number, number, number] | undefined

  get carrying(): boolean {
    return this.held !== undefined
  }

  /** The hand holding a part, or nothing. */
  get heldBy(): 'left' | 'right' | undefined {
    return this.held
  }

  /** The second hand on the part's other end, or nothing (M13-14). */
  get secondHand(): 'left' | 'right' | undefined {
    return this.second
  }

  /** Where the part in hand came from, or nothing. */
  get source(): CarrySource | undefined {
    return this.sourceNow
  }

  /**
   * One frame of both hands.
   *
   * Returns the next draft rather than mutating one, for `layoutDraft.ts`'s reason: a draft is a
   * value, and the caller is its owner.
   */
  advance(draft: LayoutDraft, hands: readonly CarryHand[], options: CarryOptions): CarryFrame {
    let next = draft
    let took = false
    let joined = false
    let dropped = false
    let committed = false
    let refused: string | undefined
    let missed: CarryMiss | undefined
    let removed: string | undefined
    let leaving = false

    const edges: Partial<Record<'left' | 'right', boolean>> = {}
    for (const hand of hands) {
      const grab = hand.present && hand.grab
      edges[hand.handedness] = grab && !this.wasGrab[hand.handedness]
      // An absent hand is remembered as closed, so it cannot reappear already pinched and take.
      this.wasGrab[hand.handedness] = hand.present ? hand.grab : true
    }

    let holder = this.held === undefined ? undefined : hands.find((h) => h.handedness === this.held)

    if (this.held !== undefined) {
      // The second hand first (M13-14): its end settles or goes back before the first hand is
      // looked at, so a frame on which both open ends the gesture with both ends where they were.
      if (this.second !== undefined) {
        const other = hands.find((h) => h.handedness === this.second)
        if (other === undefined || !other.present || !other.grab) {
          next = releaseEnd(next, 'other', other !== undefined && other.present && overBoard(other.x, other.y, other.z, options.board))
          this.second = undefined
        } else if (overBoard(other.x, other.y, other.z, options.board) && this.sourceNow?.kind === 'moved') {
          next = dragOtherTo(
            next,
            [other.x, other.y],
            snapAgainst(next, this.sourceNow.deviceId, CATCH_RADIUS / options.scale, options.symmetry),
          )
        }
      } else if (this.sourceNow?.kind === 'moved') {
        // The free hand's closing edge on the other end of the part in hand takes it.
        for (const hand of hands) {
          if (hand.handedness === this.held || edges[hand.handedness] !== true) continue
          if (hand.atPlinth !== undefined || !overBoard(hand.x, hand.y, hand.z, options.board)) continue
          const both = beginOtherDrag(next, [hand.x, hand.y], CATCH_RADIUS / options.scale, HANDLE_CATCH / options.scale)
          if (both.drag?.other === undefined) continue
          next = both
          this.second = hand.handedness
          joined = true
          break
        }
      }

      // Carrying. The holding hand opening, or vanishing, is the release — unless the second hand
      // still holds, in which case it is a hand-over: the first hand's end settles or goes back,
      // and the second hand is now the one carrying.
      if (holder === undefined || !holder.present || !holder.grab) {
        const over =
          holder !== undefined && holder.present && carriedOver(holder.x, holder.y, holder.z, options)
        if (this.second !== undefined) {
          next = releaseEnd(next, 'first', over)
          this.held = this.second
          this.second = undefined
          holder = hands.find((h) => h.handedness === this.held)
          if (holder !== undefined) next = this.carry(next, holder, options)
        } else if (this.sourceNow?.kind === 'moved' && ((holder !== undefined && removesAt(holder, options)) || this.justAtTheTray(holder, options))) {
          // Back to the tray: off the machine.
          const outcome = this.takeOff(next, this.sourceNow.deviceId, options.strings ?? BENCH_EN)
          next = outcome.draft
          committed = outcome.removed !== undefined
          removed = outcome.removed
          refused = outcome.refused
          dropped = true
        } else {
          const outcome = this.release(next, over, options.strings ?? BENCH_EN)
          next = outcome.draft
          committed = outcome.committed
          refused = outcome.refused
          dropped = true
          // Said, not silent (the 1.0.113 round): a part that went back because of where the
          // hand was, rather than a rule, says why.
          if (!over && refused === undefined) missed = missOf(holder, options)
        }
      } else {
        next = this.carry(next, holder, options)
        if (this.sourceNow?.kind === 'moved' && this.second === undefined) {
          const there = removesAt(holder, options)
          leaving = there && !this.atTrayNow
          this.atTrayNow = there
          this.sinceTrayMs = there ? 0 : this.sinceTrayMs + (options.frameMs !== undefined && options.frameMs > 0 ? options.frameMs : 1000 / 72)
        }
      }
    } else {
      // Not carrying: a closing edge on a plinth or on a placed part's handle begins one — the
      // handle lit under that hand, which is the one the author was shown.
      this.light(next, hands, options)
      for (const hand of hands) {
        if (edges[hand.handedness] !== true) continue
        const at = hand.atPlinth
        if (at !== undefined) {
          if (at.refusal !== undefined) {
            refused = at.refusal
            continue
          }
          this.held = hand.handedness
          this.sourceNow = at.hand === undefined ? { kind: 'new', plinth: at.index, device: at.kind } : { kind: 'new', plinth: at.index, device: at.kind, hand: at.hand }
          took = true
          next = this.carry(next, hand, options)
          break
        }
        if (!overBoard(hand.x, hand.y, hand.z, options.board)) continue
        const lit = this.lit[hand.handedness]
        const begun =
          lit === undefined
            ? beginDrag(next, [hand.x, hand.y], CATCH_RADIUS / options.scale, HANDLE_CATCH / options.scale)
            : beginDragOn(next, lit, [hand.x, hand.y], CATCH_RADIUS / options.scale)
        if (begun.drag === undefined) {
          // Nothing under the pinch. `beginDrag` has already cleared the selection, which is
          // the canvas's answer too: pinching empty playfield is choosing nothing.
          next = begun
          continue
        }
        next = begun
        this.held = hand.handedness
        this.lit.left = undefined
        this.lit.right = undefined
        this.sourceNow = { kind: 'moved', deviceId: begun.drag.grab.deviceId }
        took = true
        next = this.carry(next, hand, options)
        break
      }
    }

    // The source is reported on the drop frame — the host needs to know which plinth to relight —
    // and `held` is the state *after* the frame, so a hand that let go is not still "carrying".
    const source = this.sourceNow
    const ghost = this.ghost
    const inHand = this.inHand
    const inHandShift = this.inHandShift
    const snap = this.snap
    const raw = this.raw
    if (dropped) this.forget()
    return {
      draft: next,
      held: this.held,
      second: this.second,
      source,
      took,
      joined,
      dropped,
      committed,
      ...(removed === undefined ? {} : { removed }),
      ...(leaving ? { leaving } : {}),
      ghost: this.held === undefined ? undefined : ghost,
      ...(this.held !== undefined && inHand !== undefined && inHandShift !== undefined ? { inHand, inHandShift } : {}),
      snap: this.held === undefined ? undefined : snap,
      raw: this.held === undefined ? undefined : raw,
      refused,
      ...(missed === undefined ? {} : { missed }),
      hover: this.held === undefined ? this.hoverOf(next, hands, options) : undefined,
    }
  }

  /**
   * The handle under a fingertip, if any: the first present hand over the board with one lit. The
   * same question a closing edge asks, asked every frame so the answer can be shown before the
   * pinch.
   */
  private hoverOf(draft: LayoutDraft, hands: readonly CarryHand[], options: CarryOptions): CarryHover | undefined {
    this.light(draft, hands, options)
    for (const hand of hands) {
      const found = this.lit[hand.handedness]
      if (found === undefined) continue
      return { deviceId: found.deviceId, role: found.role, shapes: found.shapes, hand: hand.handedness }
    }
    return undefined
  }

  /**
   * Each hand's lit handle, this frame: the nearest within `HANDLE_CATCH`, else the one already
   * lit while the hand is within `HANDLE_KEEP` of where it is now, else none.
   */
  private light(draft: LayoutDraft, hands: readonly CarryHand[], options: CarryOptions): void {
    const devices = devicesOf(draft)
    for (const hand of hands) {
      const side = hand.handedness
      if (!hand.present || hand.atPlinth !== undefined || !overBoard(hand.x, hand.y, hand.z, options.board)) {
        this.lit[side] = undefined
        continue
      }
      const found = anchorAt(devices, [hand.x, hand.y], HANDLE_CATCH / options.scale)
      if (found !== undefined) {
        this.lit[side] = found
        continue
      }
      const kept = this.lit[side]
      const now = kept === undefined ? undefined : anchorOf(devices, kept.deviceId, kept.role)
      const keep = HANDLE_KEEP / options.scale
      this.lit[side] =
        now !== undefined && (now.at[0] - hand.x) ** 2 + (now.at[1] - hand.y) ** 2 <= keep * keep ? now : undefined
    }
  }

  /**
   * Let go without placing — the input method changed, the session recycled, the mode is leaving.
   *
   * `cancelDrag`, never `endDrag`: a carry that ends because the hand stopped existing has not
   * been released over anything, and committing where the ghost last was would plant a part at
   * the point tracking died. 06 §2.5's rule for the plunger, applied to a part.
   */
  cancel(draft: LayoutDraft): LayoutDraft {
    const next = this.held === undefined ? draft : cancelDrag(draft)
    this.forget()
    // Closed, for the reason they start closed: a hand that is *still* shut after a cancel must
    // not re-take on the very next frame (`CabinetGrab.release`).
    this.wasGrab.left = true
    this.wasGrab.right = true
    return next
  }

  private carry(draft: LayoutDraft, hand: CarryHand, options: CarryOptions): LayoutDraft {
    const source = this.sourceNow
    if (source === undefined) return draft
    const over = carriedOver(hand.x, hand.y, hand.z, options)
    const tolerance = CATCH_RADIUS / options.scale

    if (source.kind === 'moved') {
      if (!over) {
        // In the air: the drag is kept — the part is still in hand — and nothing would be placed,
        // so there is no ghost. The part is drawn at the hand instead (`inHand`): built once as it
        // leaves the board, raw and unsnapped, then moved with the hand — the remove tray too.
        this.ghost = undefined
        this.ghostAt = undefined
        this.snap = undefined
        this.raw = undefined
        // Moved whole, never reshaped (the native 1.0.210 log): a slingshot taken by one end was
        // stretched towards the hand instead of carried. The part as placed, its handle at the hand.
        const grab = draft.drag?.grab
        if (this.inHand === undefined && grab !== undefined) {
          this.inHand = devicesOf(draft).find((one) => one.id === grab.deviceId)
          this.inHandFrom = grab.from
        }
        if (this.inHand !== undefined && this.inHandFrom !== undefined && grab !== undefined) {
          this.inHandShift = [hand.x + grab.offset[0] - this.inHandFrom[0], hand.y + grab.offset[1] - this.inHandFrom[1], hand.z]
        }
        return draft
      }
      this.inHand = undefined
      this.inHandFrom = undefined
      this.inHandShift = undefined
      const next = dragTo(
        draft,
        [hand.x, hand.y],
        snapAgainst(draft, source.deviceId, tolerance, options.symmetry),
      )
      this.snap = next.drag?.snap
      const offset = next.drag?.grab.offset
      this.raw = offset === undefined ? [hand.x, hand.y] : [hand.x + offset[0], hand.y + offset[1]]
      this.ghost = previewOf(next)
      return next
    }

    if (!over) {
      this.ghost = undefined
      this.ghostAt = undefined
      this.snap = undefined
      this.raw = undefined
      return draft
    }
    const snap = snapPoint([hand.x, hand.y], {
      devices: devicesOf(draft),
      toleranceMetres: tolerance,
      symmetry: options.symmetry,
    })
    this.snap = snap
    this.raw = [hand.x, hand.y]
    // Rebuilt only when the snapped point moves, so a hand jittering inside one catch radius
    // draws one ghost rather than seventy-two a second.
    const at = this.ghostAt
    if (this.ghost === undefined || at === undefined || at[0] !== snap.at[0] || at[1] !== snap.at[1]) {
      this.ghost = makeDevice(source.device, snap.at, options.playfield, devicesOf(draft), source.hand)
      this.ghostAt = snap.at
    }
    return draft
  }

  /**
   * Take a placed part off the machine: the drag dropped, then the Builder's own `remove`, which
   * takes its wiring with it. Refused — the part back where it was — when the loader would refuse
   * what is left, with the validator's sentence.
   */
  private takeOff(
    draft: LayoutDraft,
    deviceId: string,
    strings: BenchStrings,
  ): { draft: LayoutDraft; removed: string | undefined; refused: string | undefined } {
    const back = cancelDrag(draft)
    const gone = remove(back, deviceId)
    if (gone.definition === back.definition) return { draft: back, removed: undefined, refused: undefined }
    const first = validateMachine(gone.definition).errors[0]
    if (first !== undefined) {
      return { draft: back, removed: undefined, refused: benchText(strings, 'toast.notRemoved', { message: first.message }) }
    }
    return { draft: gone, removed: deviceId, refused: undefined }
  }

  private release(
    draft: LayoutDraft,
    over: boolean,
    strings: BenchStrings,
  ): { draft: LayoutDraft; committed: boolean; refused: string | undefined } {
    const source = this.sourceNow
    if (source === undefined) return { draft, committed: false, refused: undefined }
    if (source.kind === 'moved') {
      if (!over) return { draft: cancelDrag(draft), committed: false, refused: undefined }
      const next = endDrag(draft)
      const refused = unloadable(next, strings)
      if (refused !== undefined) return { draft: cancelDrag(draft), committed: false, refused }
      return { draft: next, committed: next.definition !== draft.definition, refused: undefined }
    }
    // A new part: only a release over the board, on a ghost, places anything. `place` wires it
    // and selects it, as the palette button does.
    const ghost = this.ghost
    if (!over || ghost === undefined) return { draft, committed: false, refused: undefined }
    const placed = place(draft, ghost)
    const refused = unloadable(placed, strings)
    if (refused !== undefined) return { draft, committed: false, refused }
    return { draft: placed, committed: placed.definition !== draft.definition, refused: undefined }
  }

  private forget(): void {
    this.lit.left = undefined
    this.lit.right = undefined
    this.held = undefined
    this.second = undefined
    this.sourceNow = undefined
    this.ghost = undefined
    this.ghostAt = undefined
    this.snap = undefined
    this.raw = undefined
    this.atTrayNow = false
    this.sinceTrayMs = Number.POSITIVE_INFINITY
    this.inHand = undefined
    this.inHandFrom = undefined
    this.inHandShift = undefined
  }

  /**
   * A hand that opens just after it was in the remove tray, not back over the board, still takes
   * the part off (`TRAY_GRACE_MS`). A hand *lost* there still removes nothing: the hand stopping is
   * not a release over anything.
   */
  private justAtTheTray(hand: CarryHand | undefined, options: CarryOptions): boolean {
    if (this.sinceTrayMs > TRAY_GRACE_MS || hand === undefined || !hand.present) return false
    return !carriedOver(hand.x, hand.y, hand.z, options)
  }
}

/**
 * Would a placed part let go at this hand come off the machine: in the remove tray, and **not** over
 * the board a part can be let go over? The board wins — never at the box beside the cabinet, but the
 * rule is stated rather than assumed.
 */
export function removesAt(hand: CarryHand, options: CarryOptions): boolean {
  return hand.present && hand.atTray === true && !carriedOver(hand.x, hand.y, hand.z, options)
}
